<template>
  <!-- May 2026 paper/ink redesign — same brand language as the homepage. Each
       section is full-bleed (paper bg) and caps its content at 1400px. -->
  <div class="bg-nb-paper text-nb-ink">
    <!-- 1 — Hero ----------------------------------------------------------- -->
    <section class="overflow-hidden">
      <div
        class="mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-10 px-5 py-12 md:grid-cols-[1.08fr_0.92fr] md:gap-14 md:px-16 md:py-20"
      >
        <!-- Type -->
        <div>
          <h1
            class="m-0 font-grotesk font-black uppercase text-nb-ink text-[44px] leading-[1] tracking-[-0.045em] md:text-[clamp(44px,6vw,86px)] md:tracking-[-0.05em]"
          >
            <!-- Two lines from 375px up: from md the size follows the column width (6vw), capped at 86px.
                 Line height 1 leaves room for the É accents. -->
            Carte cadeau<br />bébé & enfant
          </h1>

          <div class="mt-7 max-w-[480px] md:mt-9">
            <p class="text-[14px] leading-[1.8] text-[#1F1F1F] md:text-[15.5px]">
              Et si on vous disait qu'il est possible d'<strong class="font-bold">offrir des souvenirs</strong>
              plutôt que des jouets et des vêtements&nbsp;? Naissance, anniversaire, Noël… c'est le
              <strong class="font-bold">cadeau original</strong> que les parents vont vraiment utiliser.
            </p>
            <p class="mt-4 text-[14px] leading-[1.8] text-[#1F1F1F] md:mt-[18px] md:text-[15.5px]">
              Avec notre carte cadeau, <strong class="font-bold">c'est IZI</strong>&nbsp;! Les parents choisissent
              les ateliers dont ils veulent profiter avec leur enfant&nbsp;: éveil musical, bébés nageurs, baby gym,
              peinture… De 25 à 1000&nbsp;€, valable 2 ans.
            </p>
          </div>

          <div
            class="mt-8 flex flex-col items-stretch gap-3.5 md:mt-9 md:flex-row md:flex-wrap md:items-center md:gap-4"
          >
            <NuxtLink to="/cartes-cadeaux/formulaire" data-attr="btn-open-gift-card-form" class="w-full md:w-auto">
              <button
                class="inline-flex w-full items-center justify-between gap-2.5 rounded-full bg-nb-ink px-5 py-3.5 text-[15px] font-bold tracking-[-0.005em] text-nb-paper transition-colors hover:bg-nb-ink-soft md:w-auto md:justify-center md:px-[26px] md:py-4 md:text-[16px]"
              >
                <span class="text-inherit">Offrir une carte cadeau</span>
                <SVGArrowRight class="h-[18px] w-[18px] shrink-0" />
              </button>
            </NuxtLink>
            <span class="font-typewriter text-[11px] uppercase tracking-[0.1em] text-nb-mute"
              >livraison instantanée par email</span
            >
          </div>
        </div>

        <!-- Kawaii gift-letter illustration (S3 → Cloudflare), tilted, with
             a red kid-font scrawl anchored to its bottom-right. -->
        <div class="flex items-center justify-center">
          <div class="relative w-[80%] max-w-[320px] md:w-full md:max-w-[480px]">
            <NuxtPicture
              provider="cloudflareImageCdn"
              :src="giftLetterImage"
              format="avif,webp"
              sizes="sm:320px md:480px"
              :width="1200"
              :height="896"
              :quality="80"
              class="block w-full"
              :img-attrs="{ class: 'block h-auto w-full rotate-[-3deg]', decoding: 'async' }"
              alt=""
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- 2 — How it works --------------------------------------------------- -->
    <section>
      <div class="mx-auto max-w-[1400px] px-5 py-12 md:px-16 md:py-20">
        <div class="flex items-baseline justify-between">
          <p class="font-typewriter text-[10px] uppercase tracking-[0.1em] text-nb-mute md:text-[11px]">
            Mode d'emploi
          </p>
        </div>
        <h2
          class="mt-2 font-grotesk font-black uppercase text-nb-ink text-[32px] leading-[0.95] tracking-[-0.04em] md:text-[54px] md:leading-[0.92] md:tracking-[-0.045em]"
        >
          Comment ça marche&nbsp;?
        </h2>

        <div class="mt-8 grid grid-cols-1 gap-5 md:mt-[52px] md:grid-cols-3 md:gap-6">
          <article
            v-for="feature in features"
            :key="feature.title"
            class="flex min-h-[200px] flex-col bg-white p-[28px_26px_26px] shadow-[0_1px_0_rgba(0,0,0,.04),0_10px_28px_rgba(0,0,0,.05)] md:min-h-[248px] md:p-[34px_30px_30px]"
          >
            <BaseBrandIcon :icon="feature.icon" class="mb-5 h-11 w-11 text-nb-splash-blue md:mb-6 md:h-12 md:w-12" />
            <h3
              class="m-0 font-grotesk text-[19px] font-extrabold leading-[1.12] tracking-[-0.02em] text-nb-ink md:text-[21px]"
            >
              {{ feature.title }}
            </h3>
            <p class="mt-3 font-typewriter text-[14px] leading-[1.7] text-[#1F1F1F]">{{ feature.body }}</p>
          </article>
        </div>
      </div>
    </section>

    <!-- 3 — Testimonial ---------------------------------------------------- -->
    <section>
      <div
        class="mx-auto grid max-w-[1400px] grid-cols-1 items-stretch gap-7 px-5 py-12 md:grid-cols-[0.86fr_1.14fr] md:gap-12 md:px-16 md:py-20"
      >
        <!-- Photo (kept: the current Marie & Robin production photo) -->
        <div class="flex items-center justify-center">
          <NuxtPicture
            provider="cloudflareImageCdn"
            :src="marieImage"
            format="avif,webp"
            sizes="sm:92vw md:520px"
            :width="800"
            :height="1000"
            :quality="80"
            class="block w-full"
            :img-attrs="{
              class:
                'block aspect-[4/5] w-full rotate-[-1.5deg] rounded-[2px] object-cover object-center shadow-[0_12px_32px_rgba(0,0,0,.12)]',
              decoding: 'async',
            }"
            alt="Marie et son fils Robin lors d'un atelier izibaby"
            loading="lazy"
          />
        </div>

        <!-- Memory card -->
        <article
          class="flex flex-col justify-center bg-white p-[32px_26px_30px] shadow-[0_1px_0_rgba(0,0,0,.04),0_12px_32px_rgba(0,0,0,.06)] md:p-[44px_48px_40px]"
        >
          <p class="mb-5 font-typewriter text-[10px] uppercase tracking-[0.1em] md:mb-[22px] md:text-[11px]">
            Souvenir de famille
          </p>
          <blockquote class="m-0">
            <p
              class="m-0 font-grotesk text-[22px] font-extrabold leading-[1.18] tracking-[-0.025em] text-nb-ink md:text-[32px]"
            >
              «&nbsp;La complicité et les souvenirs créés pendant les ateliers sont le meilleur cadeau qu'on ait pu nous
              offrir depuis la naissance de Robin&nbsp;!&nbsp;»
            </p>
          </blockquote>
          <div class="mt-6 flex items-center gap-3 border-t border-dashed border-[#D9D4C8] pt-5 md:mt-7">
            <BaseBrandIcon icon="heart-arrow" class="h-6 w-6 text-nb-ink" />
            <span class="font-grotesk text-[15px] font-extrabold tracking-[-0.01em] text-nb-ink"
              >Marie, maman de Robin 2 ans</span
            >
          </div>
          <div class="mt-7 md:mt-[30px]">
            <NuxtLink
              to="/cartes-cadeaux/formulaire"
              data-attr="btn-open-gift-card-form"
              class="inline-block w-full md:w-auto"
            >
              <button
                class="inline-flex w-full items-center justify-between gap-2.5 rounded-full bg-nb-ink px-5 py-3.5 text-[15px] font-bold tracking-[-0.005em] text-nb-paper transition-colors hover:bg-nb-ink-soft md:w-auto md:justify-center md:px-[26px] md:py-4 md:text-[16px]"
              >
                <span class="text-inherit">Offrir une carte cadeau</span>
                <SVGArrowRight class="h-[18px] w-[18px] shrink-0" />
              </button>
            </NuxtLink>
          </div>
        </article>
      </div>
    </section>

    <!-- 4 — Card preview --------------------------------------------------- -->
    <!-- Handoff note: the gift-card visual stays as the current production one
         for now; it will be reworked in a second step. -->
    <section class="overflow-hidden">
      <div class="mx-auto max-w-[1400px] px-5 py-12 md:px-16 md:py-24">
        <div class="mx-auto max-w-[640px] text-center">
          <p class="mb-3 font-typewriter text-[10px] uppercase tracking-[0.1em] text-nb-mute md:mb-3.5 md:text-[11px]">
            L'aperçu
          </p>
          <h2
            class="m-0 font-grotesk font-black uppercase text-nb-ink text-[30px] leading-[0.96] tracking-[-0.04em] md:text-[50px] md:leading-[0.94] md:tracking-[-0.045em]"
          >
            À quoi ressemble la carte cadeau&nbsp;?
          </h2>
          <p class="mt-4 font-typewriter text-[14px] leading-[1.7] text-nb-mute md:mt-[18px] md:text-[15px]">
            Laissez parler votre coeur en laissant un petit mot d'amour.
          </p>
        </div>

        <div class="mt-10 flex justify-center md:mt-14">
          <NuxtPicture
            provider="cloudflareImageCdn"
            :src="cardExampleImage"
            format="avif,webp"
            sizes="sm:88vw md:440px"
            :width="800"
            :height="1131"
            :quality="85"
            class="block w-[88%] max-w-[360px] md:w-full md:max-w-[440px]"
            :img-attrs="{
              class:
                'block aspect-[210/297] w-full rotate-[-1.5deg] rounded-[2px] object-cover object-center shadow-[0_2px_0_rgba(0,0,0,.05),0_22px_50px_rgba(0,0,0,.12)]',
              decoding: 'async',
            }"
            alt="Exemple de carte cadeau izibaby"
            loading="lazy"
          />
        </div>
      </div>
    </section>

    <!-- 5 — FAQ ------------------------------------------------------------ -->
    <section>
      <div
        class="mx-auto grid max-w-[1400px] grid-cols-1 items-start gap-9 px-5 py-12 md:grid-cols-[1fr_1.5fr] md:gap-16 md:px-16 md:py-20"
      >
        <!-- Heading column (sticky on desktop) -->
        <div class="md:sticky md:top-20">
          <p
            class="mb-2.5 font-typewriter text-[10px] uppercase tracking-[0.1em] text-nb-mute md:mb-3.5 md:text-[11px]"
          >
            FAQ · cartes cadeaux
          </p>
          <h2
            class="m-0 font-grotesk font-black uppercase text-nb-ink text-[36px] leading-[0.94] tracking-[-0.04em] md:text-[56px] md:leading-[0.92] md:tracking-[-0.045em]"
          >
            On vous<br />répond.
          </h2>
          <p
            class="mt-4 max-w-[320px] font-typewriter text-[13px] leading-[1.7] text-nb-mute md:mt-[22px] md:text-[14px]"
          >
            Tout ce qu'on nous demande le plus souvent sur les cartes cadeaux. <br />Une autre question&nbsp;? On est
            ultra-réactifs sur WhatsApp&nbsp;:
          </p>

          <!-- WhatsApp pill -->
          <a
            href="https://wa.me/33771084272"
            target="_blank"
            rel="noopener"
            data-attr="btn-gift-faq-whatsapp"
            class="mt-[18px] inline-flex items-center gap-3 rounded-full bg-[#25D366] p-[8px_14px_8px_10px] no-underline md:mt-7 md:p-[10px_18px_10px_12px]"
          >
            <span
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[rgba(255,255,255,0.18)] text-white md:h-[38px] md:w-[38px]"
            >
              <svg
                class="h-[18px] w-[18px] md:h-[22px] md:w-[22px]"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  d="M19.05 4.91A9.82 9.82 0 0 0 12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.46 1.32 4.96L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91a9.82 9.82 0 0 0-2.91-7.02zM12.04 20.15h-.01a8.23 8.23 0 0 1-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.22 8.22 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24a8.18 8.18 0 0 1 5.83 2.42 8.18 8.18 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.23 8.23zm4.52-6.16c-.25-.13-1.47-.72-1.69-.8-.23-.08-.39-.13-.55.13-.16.25-.64.8-.78.96-.14.16-.29.18-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.38-1.72-.14-.25-.02-.39.11-.51.11-.11.25-.29.37-.43.13-.14.16-.25.25-.41.08-.16.04-.31-.02-.43-.06-.13-.55-1.33-.76-1.82-.2-.48-.41-.41-.55-.42-.14 0-.31-.02-.47-.02s-.43.06-.66.31c-.23.25-.86.84-.86 2.05 0 1.21.88 2.37 1 2.54.12.16 1.73 2.64 4.19 3.7.58.25 1.05.4 1.4.51.59.19 1.13.16 1.55.1.47-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.15-1.18-.06-.1-.23-.17-.48-.29z"
                />
              </svg>
            </span>
            <span class="flex flex-col text-left leading-[1.15]">
              <span class="text-[9px] font-bold uppercase tracking-[0.1em] text-[rgba(255,255,255,0.85)] md:text-[10px]"
                >WhatsApp</span
              >
              <span class="font-grotesk text-[14px] font-extrabold tracking-[-0.01em] text-white md:text-[16px]"
                >+33 7 71 08 42 72</span
              >
            </span>
          </a>
        </div>

        <!-- Accordion — gift-card questions, first open by default -->
        <BaseFAQ :items="giftFaqData" :default-open-index="0" />
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import type { FaqItem } from "@/composables/faqData";

useSeoMeta({
  title: "Carte cadeau naissance, anniversaire : ateliers bébé & enfant | IZIBABY",
  description:
    "Naissance, anniversaire, Noël : la carte cadeau IZIBABY, valable 2 ans sur 200+ ateliers bébé et enfant (Paris, Lyon, Bordeaux, Lille, Bayonne). Reçue en PDF.",
  ogTitle: "Carte cadeau naissance, anniversaire : ateliers bébé & enfant | IZIBABY",
  ogDescription:
    "Naissance, anniversaire, Noël : la carte cadeau IZIBABY, valable 2 ans sur plus de 200 ateliers bébé et enfant à Paris, Lyon, Bordeaux, Lille et Bayonne.",
});

// Imagery lives on S3; Cloudflare derives the responsive AVIF/WebP variants.
// The gift-letter master is shared with the homepage gift-card section.
const { awsBucketURL } = useRuntimeConfig().public;
const giftLetterImage = `${awsBucketURL}izibaby/static_items/illustration-gift-letter.png`;
const marieImage = `${awsBucketURL}izibaby/static_items/page_cartes_cadeaux/marie_et_robin.jpg`;
const cardExampleImage = `${awsBucketURL}izibaby/static_items/page_cartes_cadeaux/carte_cadeau_example_v2.png`;

// "Comment ça marche ?" — a single blue brand icon per card (no stickers).
const features = [
  {
    icon: "calendar",
    title: "Valable 2 ans",
    body: "Sans pression ! Nos cartes sont valables sur tous les ateliers pendant 2 ans.",
  },
  {
    icon: "comet",
    title: "Dispo instantanément",
    body: "Recevez la carte cadeau en PDF directement par email, prête à imprimer ou à transférer.",
  },
  {
    icon: "map-pin",
    title: "Paris + Lyon + Bordeaux + Lille + Bayonne",
    body: "À utiliser sur plus de 200 ateliers bébé et enfant à Paris, Lyon, Bordeaux, Lille et Bayonne.",
  },
];

// Gift-card-specific FAQ (distinct from the shared site FAQ). Hand-authored HTML
// answers — internal links route client-side, hello@izibaby.fr is a real mailto.
const giftFaqData: FaqItem[] = [
  {
    id: "occasions-carte-cadeau",
    question: "Pour quelle occasion offrir une carte cadeau ?",
    answer: `Pour toutes&nbsp;! Une naissance, une baby shower, le premier anniversaire, les 3 ans, Noël… C'est un cadeau original qui ne finira pas au fond d'un placard&nbsp;: les parents s'offrent des moments avec leur enfant (massage bébé, éveil musical, bébés nageurs, baby gym, peinture…) quand ils le souhaitent. Avec 2 ans de validité, ils ont tout le temps de choisir.`,
  },
  {
    id: "offrir-avant-naissance",
    question: "Peut-on l'offrir avant la naissance ?",
    answer: `Oui&nbsp;! La carte est aussi valable sur nos ateliers pour les futurs parents, comme le <a href="/ateliers/paris/yoga-prenatal">yoga prénatal à Paris</a> ou <a href="/ateliers/lyon/yoga-prenatal">à Lyon</a>. Idéal pour une baby shower.`,
  },
  {
    id: "quel-montant",
    question: "Quel montant offrir ?",
    answer: `Vous choisissez librement entre 25&nbsp;€ et 1000&nbsp;€. Le montant le plus offert est 50&nbsp;€, de quoi réserver un ou deux ateliers. La carte peut s'utiliser en plusieurs fois.`,
  },
  {
    id: "utiliser-carte-cadeau",
    question: "Comment utiliser ma carte cadeau ?",
    answer: `Il faut indiquer son code cadeau au moment de la réservation dans l'encart «&nbsp;Utiliser un code de carte cadeau&nbsp;». La carte cadeau est utilisable en plusieurs fois, sur différents ateliers. Pour voir combien il reste sur votre carte cadeau, vous pouvez vous rendre sur la page <a href="/verifier-ma-carte-cadeau">Vérifier ma carte cadeau</a>. Si le montant de l'atelier est supérieur à celui de la carte, il suffit de payer le complément. Notre souhait est vraiment qu'aucune carte cadeau ne reste inutilisée donc on vous enverra des mails de rappel pour qu'elle ne finisse pas aux oubliettes&nbsp;!`,
  },
  {
    id: "offrir-atelier-precis",
    question: "Puis-je offrir un atelier que j'ai choisi plutôt qu'un montant ?",
    answer: `Aucun souci non plus, il suffit de nous envoyer un mail à <a href="mailto:hello@izibaby.fr">hello@izibaby.fr</a>. Nous personnaliserons la carte pour ajouter le nom de l'atelier que vous souhaitez offrir au lieu du montant.`,
  },
  {
    id: "duree-validite",
    question: "Combien de temps est-elle valable ?",
    answer: `Nos cartes cadeaux sont valables 2 ans sur tous les ateliers présents sur <a href="/">izibaby.fr</a>. L'idée est de vous laisser le temps de choisir les ateliers qui vous plaisent le plus, sans pression.`,
  },
  {
    id: "plusieurs-ateliers",
    question: "Puis-je utiliser ma carte cadeaux pour plusieurs ateliers ?",
    answer: `Oui, c'est pensé pour&nbsp;! Vous pouvez réserver autant d'ateliers que vous le souhaitez. Votre code cadeau restera valable tant qu'il reste de l'argent sur votre carte, et dans la limite des 2 ans de validité.`,
  },
  {
    id: "reception-carte",
    question: "Quand et sous quelle forme vais-je recevoir la carte cadeau ?",
    answer: `Elle sera envoyée automatiquement par mail juste après la confirmation du paiement. Si vous n'avez rien reçu, pensez à bien regarder dans vos courriers indésirables, et si toujours rien, envoyez-nous un mail à <a href="mailto:hello@izibaby.fr">hello@izibaby.fr</a> pour que nous puissions mener notre petite enquête.`,
  },
  {
    id: "qui-recoit",
    question: "Qui reçoit la carte cadeau ?",
    answer: `Les cartes cadeaux sont envoyées au mail que vous renseignez sur le formulaire.`,
  },
  {
    id: "montant-restant",
    question: "Comment voir le montant restant sur ma carte cadeau ?",
    answer: `Vous pouvez voir le montant restant sur la page <a href="/verifier-ma-carte-cadeau">Vérifier ma carte cadeau</a>.`,
  },
  {
    id: "recue-comment-utiliser",
    question: "Je viens de recevoir une carte cadeau, comment l'utiliser ?",
    answer: `Rien de plus simple, il suffit de renseigner le code de votre carte cadeau dans l'encart «&nbsp;code cadeau&nbsp;» sur le formulaire d'inscription de l'atelier de votre choix.`,
  },
];
</script>
